import React, { useEffect, useState } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus, faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import { SERVER_BASE } from "../config/server";
import apiClient from "../utils/apiClient";

const ItemConversion = ({ accessOfDepartments }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const editData = location?.state?.editData;
    const [editId, setEditId] = useState(null);
    const token = localStorage.getItem("token");
    const code = localStorage.getItem("code");
    const sewadarId = localStorage.getItem("sewadarId");
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState(null);
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [itemOptions, setItemOptions] = useState([]);
    const [fromItems, setFromItems] = useState([{ item: null, quantity: "" }]);
    const [toItems, setToItems] = useState([{ item: null, quantity: "" }]);
    useEffect(() => {
        if (accessOfDepartments) {
            const deptArray = accessOfDepartments
                .split(',')
                .map((dept) => ({ value: dept.trim(), label: dept.trim() }));
            setDepartments(deptArray);
        }
    }, [accessOfDepartments]);
    // Handle Edit Mode
    const handleEdit = (data) => {
    
        setEditId(data.id);
        setSelectedDept({ label: data?.department, value: data?.department });
        setSelectedLocation({ label: data?.location, value: data?.location });
    
        setFromItems(
            (data.fromItems || []).map((f) => ({
                item: {
                    value: f.itemName,
                    itemId: f.itemId,
                    label: f.itemName,
                    uom: f.uom,
                },
                quantity: f.quantity,
            }))
        );
    
        setToItems(
            (data.toItems && data.toItems.length > 0
                ? data.toItems
                : [
                      {
                          itemId: data.toItemId,
                          itemName: data.toItemName,
                          quantity: data.toQuantity,
                          uom: data.toUom,
                      },
                  ]
            ).map((t) => ({
                item: {
                    value: t.itemName,
                    label: t.itemName,
                    itemId: t.itemId,
                    uom: t.uom,
                },
                quantity: t.quantity,
            }))
        );
    };
    
    useEffect(() => {
        if (editData) {
            handleEdit(editData);
        }
    }, [editData]);

    // Fetch Item Options
    const fetchItemOptions = async (accessOfDepartmentsArray) => {
        try {
            const response = await apiClient.get(`${SERVER_BASE}/getItemOptionsByDepartment`, {
                params: {
                    code,
                    accessOfDepartments: accessOfDepartmentsArray.join(','),
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setItemOptions(response.data.map((item) => ({
                value: item.printName,
                label: `${item.printName}${item.additionalNames ? ` - [ ${item.additionalNames} ]` : ''}`,
                itemId: item.id,
                itemCode: item.itemCode,
                uom: item.uom,
                unit: item.unit,
            })));
        } catch (error) {
            console.error('Error fetching item options:', error.message);
            console.error('Details:', error.response?.data || error.message);
        }
    };
    const fetchLocations = () => {
        apiClient
            .get(SERVER_BASE + "/getLocations", {
                params: { code }, headers: {
                    Authorization: `Bearer ${token}`, // Add the JWT token in the header
                }
            })
            .then((response) =>
                setLocations(
                    response.data.map((location) => ({
                        value: location.location,
                        label: location.location,
                    }))
                )
            )
            .catch((error) => console.error(error));
    };
    useEffect(() => {
        fetchLocations();
    }, []);

    // Handle From Item Changes
    const handleFromChange = (index, field, value) => {
        const updated = [...fromItems];
        if (field === "item") {
            if (fromItems.some((f, i) => i !== index && f.item?.value === value?.value)) {
                Swal.fire({
                    toast: true,
                    icon: "warning",
                    title: "This item is already selected in From section",
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                return;
            }
        }
        updated[index][field] = value;
        setFromItems(updated);
    };

    // Handle To Item Changes
    const handleToChange = (index, field, value) => {
        const updated = [...toItems];
        if (field === "item") {
            if (toItems.some((t, i) => i !== index && t.item?.value === value?.value)) {
                Swal.fire({
                    toast: true,
                    icon: "warning",
                    title: "This item is already selected in To section",
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                return;
            }
        }
        updated[index][field] = value;
        setToItems(updated);
    };

    // Add/Remove Rows
    const addFromRow = () => setFromItems([...fromItems, { item: null, quantity: "" }]);
    const removeFromRow = (index) => {
        if (fromItems.length === 1) return;
        setFromItems(fromItems.filter((_, i) => i !== index));
    };

    const addToRow = () => setToItems([...toItems, { item: null, quantity: "" }]);
    const removeToRow = (index) => {
        if (toItems.length === 1) return;
        setToItems(toItems.filter((_, i) => i !== index));
    };

    // Submit Handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedDept) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: 'Please select department',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
            return;
        }
        if (!selectedLocation) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: 'Please select location',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            }); 
            return;
        }
        if (fromItems.some((f) => !f.item || !f.quantity)) {
            Swal.fire({
                toast: true,
                icon: "warning",
                title: "All From items and quantities are required",
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
            return;
        }

        if (toItems.some((t) => !t.item || !t.quantity)) {
            Swal.fire({
                toast: true,
                icon: "warning",
                title: "All To items and quantities are required",
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
            return;
        }

        const fromUoms = [...new Set(fromItems.map((f) => f.item.uom))];
        const toUoms = [...new Set(toItems.map((t) => t.item.uom))];
        if (
            fromUoms.length > 1 ||
            toUoms.length > 1 ||
            fromUoms[0] !== toUoms[0]
        ) {
            const confirmResult = await Swal.fire({
                title: "UOM Mismatch",
                text: "The UOM of items are different. Do you want to continue?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, continue",
                cancelButtonText: "Cancel",
            });
            if (!confirmResult.isConfirmed) return;
        }

        const payload = {
            fromItems: fromItems.map((f) => ({
                fromItemId: f.item.itemId,
                fromItemName: f.item.value,
                fromQuantity: f.quantity,
                fromUom: f.item.uom,
            })),
            toItems: toItems.map((t) => ({
                toItemId: t.item.itemId,
                toItemName: t.item.value,
                toQuantity: t.quantity,
                toUom: t.item.uom,
            })),
            createdBy: sewadarId,
            department:selectedDept?.value,
            location:selectedLocation?.value,
            centerCode:code
        };

        try {
            if (editId) {
                await apiClient.put(`${SERVER_BASE}/update-item-conversion/${editId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                Swal.fire({
                    toast: true,
                    icon: "success",
                    title: "Conversion updated successfully",
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
            } else {
                await apiClient.post(`${SERVER_BASE}/save-item-conversion`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                Swal.fire({
                    toast: true,
                    icon: "success",
                    title: "Conversion saved successfully",
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
            }

            navigate("../ItemConversionList");
        } catch (error) {
            console.error("Error saving/updating conversion:", error);
            Swal.fire({
                toast: true,
                icon: "error",
                title: "Error saving/updating conversion",
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
        }
    };

    return (
        <div className="formcontainer mt-5">
            <div className="d-flex align-items-end mb-3">
                <button
                    className="btn btn-secondary btn-sm me-3"
                    onClick={() => navigate("../ItemConversionList")}
                >
                    <FontAwesomeIcon icon={faChevronCircleLeft} /> Back
                </button>
                <h5>{editId ? "Edit Conversion" : "Conversion"}</h5>
            </div>
            <div className="container mt-4">
                <form onSubmit={handleSubmit}>
                    <div className="row mb-2">
                        <div className="col-md-3">
                            <label className="form-label">Department</label>
                            <Select
                                options={departments}
                                value={selectedDept}
                                onChange={(selectedOption) => {
                                    setSelectedDept(selectedOption);
                                    if (selectedOption) {
                                        fetchItemOptions([selectedOption.value]); // Wrap in array if your API expects multiple departments
                                    }
                                }}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Location</label>
                            <Select
                                options={locations}
                                value={selectedLocation}
                                onChange={(selectedOption) => {
                                    setSelectedLocation(selectedOption);
                                }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        {/* FROM ITEMS */}
                        <div className="col-md-6">
                            <div className="border rounded p-2">
                                <h6 className="fw-bold" >Consumption</h6>
                                <div className="row fw-bold mb-2" style={{ fontSize: "15px" }}>
                                    <div className="col-6">Item</div>
                                    <div className="col-2">Qty</div>
                                    <div className="col-2">UOM</div>
                                    <div className="col-2">Actions</div>
                                </div>
                                {fromItems.map((f, index) => (
                                    <div className="row align-items-center mb-1" key={index} style={{ fontSize: "15px" }}>
                                        <div className="col-6">
                                            <Select
                                                options={itemOptions}
                                                value={f.item}
                                                onChange={(selected) => handleFromChange(index, "item", selected)}
                                                styles={{ control: (base) => ({ ...base, minHeight: "30px", fontSize: "15px" }) }}
                                            />
                                        </div>
                                        <div className="col-2">
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={f.quantity}
                                                onChange={(e) => handleFromChange(index, "quantity", e.target.value)}
                                            />
                                        </div>
                                        <div className="col-2">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={f.item?.uom || ""}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-2 d-flex">
                                            <button type="button" className="btn btn-success btn-sm me-1" onClick={addFromRow}>
                                                <FontAwesomeIcon icon={faPlus} />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => removeFromRow(index)}
                                                disabled={fromItems.length === 1}
                                            >
                                                <FontAwesomeIcon icon={faMinus} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* TO ITEMS */}
                        <div className="col-md-6">
                            <div className="border rounded p-2">
                                <h6 className="fw-bold" >Production</h6>
                                <div className="row fw-bold mb-2" style={{ fontSize: "15px" }}>
                                    <div className="col-6">Item</div>
                                    <div className="col-2">Qty</div>
                                    <div className="col-2">UOM</div>
                                    <div className="col-2">Actions</div>
                                </div>
                                {toItems.map((t, index) => (
                                    <div className="row align-items-center mb-1" key={index} style={{ fontSize: "15px" }}>
                                        <div className="col-6">
                                            <Select
                                                options={itemOptions}
                                                value={t.item}
                                                onChange={(selected) => handleToChange(index, "item", selected)}
                                                styles={{ control: (base) => ({ ...base, minHeight: "30px", fontSize: "15px" }) }}
                                            />
                                        </div>
                                        <div className="col-2">
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={t.quantity}
                                                onChange={(e) => handleToChange(index, "quantity", e.target.value)}
                                            />
                                        </div>
                                        <div className="col-2">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={t.item?.uom || ""}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-2 d-flex">
                                            <button type="button" className="btn btn-success btn-sm me-1" onClick={addToRow}>
                                                <FontAwesomeIcon icon={faPlus} />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => removeToRow(index)}
                                                disabled={toItems.length === 1}
                                            >
                                                <FontAwesomeIcon icon={faMinus} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* SAVE BUTTON */}
                    <div className="mt-3">
                        <button type="submit" className="btn btn-success btn-sm px-4">
                            {editId ? "Update" : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ItemConversion;
